import { IRootScopeService, IQService, IPromise, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IUser } from "@gtmhub/users/models";
import { AutomationTriggerType, IAutomation, IAutomationHistory, IAutomationHistoryQueryParams } from "../models/automation";
import { AutomationService } from "../services/automation";
import { UserService } from "@gtmhub/users/userService";
import { GoalService } from "@gtmhub/goals/goalService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ICollection } from "@gtmhub/models";
import { IGoal } from "@gtmhub/goals/models";
import { IStateInit } from "@gtmhub/core/routing";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { getCurrentUserId } from "@gtmhub/users";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions, IAssigneeIdMap, IAssigneesState, IAssigneesStoreState } from "@gtmhub/assignees";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";

interface IAutomationHistoryScope extends IScope, IExtendedRootScopeService, IAutomationHistoryReduxScope {
  currentUser: IUser;
  currentAutomation: IAutomation<AutomationTriggerType>;

  indicators: {
    loading: boolean;
  };

  currentPage: number;
  totalPages: number;
  onPageChange(page: number): void;

  history: IAutomationHistory[];

  openHistoryItem(historyItem: IAutomationHistory): void;
}

interface IAutomationHistoryStateParams {
  automationId: string;
}

const itemsPerPage = 10;

interface IAutomationHistoryReduxScope {
  assigneesIdMap: IAssigneeIdMap;
  assigneesState: IAssigneesState;
}

const bindStateToScope = (state: IAssigneesStoreState): IAutomationHistoryReduxScope => ({
  assigneesIdMap: state.assignees.map,
  assigneesState: state.assignees,
});

export class AutomationHistoryCtrl implements IStateInit {
  public static $inject = [
    "$state",
    "$scope",
    "$rootScope",
    "$stateParams",
    "$q",
    "AutomationService",
    "UserService",
    "GoalService",
    "MetricService",
    "UIErrorHandlingService",
    "$ngRedux",
    "AssigneeActions",
  ];

  constructor(
    private $state: StateService,
    private $scope: IAutomationHistoryScope,
    private $rootScope: IRootScopeService,
    private $stateParams: IAutomationHistoryStateParams,
    private $q: IQService,
    private automationService: AutomationService,
    private userService: UserService,
    private goalService: GoalService,
    private metricService: MetricService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions
  ) {
    this.$rootScope.$broadcast("$automationStateChange");

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope.openHistoryItem = this.openHistoryItem;

    this.$scope.indicators = {
      loading: true,
    };
    this.$scope.currentPage = 1;
    this.$scope.onPageChange = this.onPageChange;
  }

  public stateInit(): IPromise<unknown> {
    return this.$scope.watchUntil(() => this.$scope.assigneesState.isFetched).then(() => this.init());
  }

  private historyStrings: { states: { [key in AutomationTriggerType]: string }; statusFilter: string } = {
    states: {
      goal: "gtmhub.goals.all._goalSidebar.goal",
      metric: "gtmhub.goals.all._goalSidebar.goal._metric.metric",
      user: "gtmhub.employees.employee",
    },
    statusFilter: "success,fail",
  };

  private init = (): IPromise<unknown> => {
    const queryParams: IAutomationHistoryQueryParams = {
      skip: (this.$scope.currentPage - 1) * itemsPerPage,
      take: itemsPerPage,
      status: this.historyStrings.statusFilter,
    };

    return this.$q
      .all({
        currentUser: this.userService.getUser(getCurrentUserId()),
        currentAutomation: this.automationService.getAutomation(this.$stateParams.automationId),
        history: this.automationService.getAutomationHistory(this.$stateParams.automationId, queryParams),
      })
      .then(
        (map: { currentUser: IUser; currentAutomation: IAutomation<AutomationTriggerType>; history: ICollection<IAutomationHistory> }) => {
          this.$scope.currentUser = map.currentUser;
          this.$scope.currentAutomation = map.currentAutomation;
          this.$scope.totalPages = Math.ceil(map.history.totalCount / itemsPerPage);

          return this.enrichHistory(map.history.items);
        },
        (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: false })
      );
  };

  private enrichHistory = (historyCollection: IAutomationHistory[]): IPromise<unknown> => {
    if (!historyCollection.length) {
      this.$scope.indicators.loading = false;
      return this.$q.resolve();
    }

    const historyPromises: IPromise<IGoal | IMetric>[] = historyCollection.reduce((promiseArr, history) => {
      // natively this.$q.all of array is being aborted when of its elements get status 404 and aborts the whole array
      // having 5 promises and 1 of them is rejected cuz of 404 will lead the array to be []
      // using angular.identity in the catch block will return the fully fullfilled value of the promise
      // either it is rejected or resolved
      if (history.targetType === "goal") {
        promiseArr.push(this.goalService.getGoal(history.targetId).catch(angular.identity));
      } else if (history.targetType === "metric") {
        this.metricService.getMetric(history.targetId).catch(angular.identity);
      }

      return promiseArr;
    }, []);

    return this.$q.all(historyPromises).then(
      (map: (IGoal | IMetric)[]) => {
        historyCollection.forEach((history: IAutomationHistory) => {
          if (history.targetType === "user") {
            const targetUser = this.$scope.assigneesIdMap[history.targetId];
            history.targetName = targetUser && targetUser.name;
            return;
          }

          map.forEach((item: IGoal | IMetric) => {
            if (history.targetType === "goal" && history.targetId === item.id) {
              history.targetName = item.name;
              history.targetSessionId = item.sessionId;
            }
            if (history.targetType === "metric" && history.targetId === item.id) {
              history.targetName = item.name;
              history.targetSessionId = item.sessionId;
              history.targetParentId = (item as IMetric).goalId;
            }
          });
        });

        this.$scope.history = historyCollection;
        this.$scope.indicators.loading = false;
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private openHistoryItem = (historyItem: IAutomationHistory): void => {
    switch (historyItem.targetType) {
      case "goal": {
        this.$state.go(this.historyStrings.states.goal, { id: historyItem.targetId, planningSessionId: historyItem.targetSessionId });
        return;
      }
      case "metric": {
        this.$state.go(this.historyStrings.states.metric, {
          id: historyItem.targetParentId,
          planningSessionId: historyItem.targetSessionId,
          metricId: historyItem.targetId,
        });
        return;
      }
      case "user": {
        this.$state.go(this.historyStrings.states.user, { employeeId: historyItem.targetId });
        return;
      }
    }
  };

  private getSomeHistoryItems = (): void => {
    const queryParams: IAutomationHistoryQueryParams = {
      skip: (this.$scope.currentPage - 1) * itemsPerPage,
      take: itemsPerPage,
      status: this.historyStrings.statusFilter,
    };

    this.automationService.getAutomationHistory(this.$stateParams.automationId, queryParams).then(
      (history: ICollection<IAutomationHistory>) => {
        this.$scope.totalPages = Math.ceil(history.totalCount / itemsPerPage);
        this.enrichHistory(history.items);
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: false })
    );
  };

  private onPageChange = (page: number) => {
    this.$scope.currentPage = page;
    this.$scope.indicators.loading = true;

    this.getSomeHistoryItems();
  };
}
